import _ from 'lodash';
import React from 'react';
import {
  getAuth,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  addDoc,
  getDocs,
  collection,
  onSnapshot,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  getDatabase,
  ref,
  onValue,
  remove,
} from 'firebase/database';
import { eventChannel, channel } from 'redux-saga';
import {
  all,
  takeLatest,
  put,
  call,
  fork,
  select,
  takeEvery,
  take,
  race,
} from 'redux-saga/effects';
import axios from 'axios';
import moment from 'moment-timezone';
import { v1 as uuidv1 } from 'uuid';
import { Modal, Button, notification as notificationAntd } from 'antd';
import actions from './actions';
import appActions from '../app/actions';
import authActions from '../auth/actions';
import agendaActions from '../agenda/actions';
import { notification } from '../../components';
import {
  startDiagnostics,
  flushDiagnostics,
  flushDiagnosticsBeacon,
  collectStatsSnapshot,
  recordGum,
  recordIceConfigSource,
  recordPatientPresenceSeen,
} from './diagnostics';
import {
  startPatientFunnel,
  markFunnelMedia,
  markFunnelEntered,
  markFunnelReachedWebrtc,
  flushPatientFunnel,
  flushPatientFunnelBeacon,
} from './patientFunnel';

const ROOT_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_ROOT_URL;

let peerConnection = null;
let dataChannel = null;
let localStream = null;
let remoteStream = null;
// Remote ICE candidates that arrived before setRemoteDescription. Adding them
// early throws InvalidStateError (Safari/spec), so buffer and flush once
// remoteDescription is set.
let pendingRemoteCandidates = [];
// Telemetry flags: count each consult at most once (TURN vs direct). Reset on the
// real call end (END_TELEMED_ROOM_SUCCESS).
let telemedConsultCounted = false;
let telemedTurnCounted = false;

const configuration = {
  iceServers: [
    {
      urls: [
        'stun:stun1.l.google.com:19302',
        'stun:stun2.l.google.com:19302',
      ],
    },
  ],
  iceCandidatePoolSize: 10,
};

// After this much time without a successful connection, stop auto-retrying and
// surface the manual "Tentar novamente" button (both doctor and patient).
const CONNECTION_DEADLINE_MS = 180000; // 3 minutes

// Offer older than this = zombie room (stale doc): patient keeps waiting instead
// of connecting against it. A live offer never exceeds ~3min (doctor's deadline
// recreates it), so this is a generous margin against clock skew.
const STALE_OFFER_MS = 10 * 60 * 1000; // 10 minutes

// The patient re-writes presence every HEARTBEAT_MS while waiting; the doctor
// ignores flags older than PRESENCE_STALE_MS (a few missed beats) and re-checks on
// a PRESENCE_SWEEP_MS timer so a flag without heartbeats clears even with no RTDB
// event (crash / network drop / OS killing the tab without a pagehide beacon).
const HEARTBEAT_MS = 30 * 1000; // 30s
const PRESENCE_STALE_MS = 2 * 60 * 1000; // 2 minutes (~4 missed beats)
const PRESENCE_SWEEP_MS = 30 * 1000; // 30s

// Safety net for a forgotten open call: a TURN-relayed (paid) call burns
// bandwidth while the peerConnection stays open. After this long connected, ask
// to continue; if nobody answers, end it (frees the relay). Doesn't cut a real
// consult — only an abandoned one.
const MAX_CALL_DURATION_MS = 3 * 60 * 60 * 1000; // 3 hours
const MAX_DURATION_PROMPT_MS = 120000; // 2 min to answer before ending

function sleep(ms) {
  // Resolve with `true` so it can be used as the winning branch of a saga
  // `race` (e.g. `const { timeout } = race({ timeout: call(sleep, ms) })`).
  // A bare `setTimeout(resolve)` resolves to `undefined`, which would make the
  // `if (timeout)` check always false.
  return new Promise((resolve) => {
    setTimeout(() => resolve(true), ms);
  });
}

// ---- Remote audio / autoplay (Safari/iOS) ---------------------------------
// Safari (desktop and iOS) blocks media autoplay WITH audio outside a user
// gesture. We play the element and handle the rejection: on NotAllowedError we
// play muted (video shows) and ask for a tap to unmute. The AudioContext (also
// used by the diagnostics audio meter) starts 'suspended' until a gesture, so
// it's only created/resumed on the gesture.
let sharedAudioContext = null;

function getSharedAudioContext() {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!sharedAudioContext) {
    try {
      sharedAudioContext = new Ctx();
    } catch (error) {
      console.warn('AudioContext creation failed', error);
      return null;
    }
  }
  return sharedAudioContext;
}

function resumeSharedAudioContext() {
  if (sharedAudioContext && sharedAudioContext.state === 'suspended' && sharedAudioContext.resume) {
    return sharedAudioContext.resume().catch((error) => console.warn('AudioContext resume failed', error));
  }
  return Promise.resolve();
}

function playMediaElement(el) {
  try {
    const maybePromise = el.play();
    return maybePromise && typeof maybePromise.then === 'function'
      ? maybePromise
      : Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
}

function getTelemedIceServersRequest({ roomId, mainUser }, timeout) {
  return axios.post(`${ROOT_URL}/getTelemedIceServers`, { roomId, mainUser }, { timeout });
}

// Fetches ephemeral STUN/TURN credentials (Twilio NTS) right before creating a
// RTCPeerConnection. The TURN relay lets the call traverse symmetric NAT / CGNAT
// (mobile) / restrictive firewalls, where STUN-only fails. Tries 2× before
// falling back to static STUN-only. Timeouts are generous on purpose (45s/30s):
// the Cloud Function cold start (admin + Twilio SDK) can exceed 10s and still
// succeed — we don't want STUN-only just from slowness. The timeout only avoids
// freezing on a hung request; it stays well under CONNECTION_DEADLINE_MS (3 min).
// Records the source used (iceConfigSource) in diagnostics.
function* buildPeerConnectionConfiguration(roomId, mainUser) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const timeout = attempt === 0 ? 45000 : 30000;
      const response = yield call(getTelemedIceServersRequest, { roomId, mainUser }, timeout);
      const iceServers = response?.data?.iceServers;
      if (iceServers && iceServers.length) {
        recordIceConfigSource('turn');
        return { iceServers, iceCandidatePoolSize: 10 };
      }
    } catch (error) {
      console.warn(`getTelemedIceServers attempt ${attempt + 1} failed`, error);
      if (attempt === 0) yield call(sleep, 500);
    }
  }
  console.warn('Falling back to STUN-only ICE configuration');
  recordIceConfigSource('stun-fallback');
  return configuration;
}

// Inspects the selected ICE candidate pair to tell whether the connection is
// going through a TURN relay (paid) or a direct/STUN path (free). Reuses the
// shared getStats snapshot (also used by diagnostics).
function* didConnectionUseTurn(pc) {
  const snapshot = yield call(collectStatsSnapshot, pc);
  return !!(snapshot && snapshot.usedTurn);
}

// Records a telemed connection stat (connected | turn) via a Cloud Function, so
// the increment is done with admin and no client-facing RTDB rules are needed.
function incrementTelemedStatRequest(field, professionalId) {
  return axios.post(`${ROOT_URL}/recordTelemedStat`, { field, professionalId });
}

// Presence signal. The patient is anonymous (no Firebase Auth), so it writes via
// a cloud function (same trust model as recordTelemedPatientFunnel): 'waiting' on
// entering the waiting room, 'gone' on leaving. The function (admin) resolves the
// patient name/userInfo from the appointment and writes
// users/{mainUser}/telemedPresence/{roomId}, which the doctor reads.
function setTelemedPresenceRequest({ mainUser, roomId, status }) {
  return axios.post(`${ROOT_URL}/setTelemedPresence`, { mainUser, roomId, status });
}

// 'gone' on page unload (tab close / F5). Uses fetch keepalive instead of axios
// because the browser cancels in-flight XHR on unload; keepalive lets the request
// finish after the page is gone.
function setTelemedPresenceGoneOnUnload({ mainUser, roomId }) {
  return fetch(`${ROOT_URL}/setTelemedPresence`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mainUser, roomId, status: 'gone' }),
    keepalive: true,
  });
}

const getMainUserFromStore = (state) => state.Auth.mainUser;

const getIsProfessionalFromStore = (state) => state.Auth.professional;

const getTelemedMainUserFromStore = (state) => state.Telemed.telemedMainUser;

const getUserTypeFromStore = (state) => state.Telemed.userType;

const getSessionUuidFromStore = (state) => state.Telemed.sessionUuid;

const getOwnMicMutedFromStore = (state) => state.Telemed.ownMicMuted;

const getUserMediaWebcamDisabledFromStore = (state) => state.Telemed.userMediaWebcamDisabled;

const getUserPatientMediaWebcamDisabledFromStore = (state) => state.Telemed.userPatientMediaWebcamDisabled;

const getNoVideoDevicesFromStore = (state) => state.Telemed.noVideoDevices;

const getNoAudioDevicesFromStore = (state) => state.Telemed.noAudioDevices;

const getDataChannelOpenFromStore = (state) => state.Telemed.dataChannelOpen;

const getCreateRoomAttemptsFromStore = (state) => state.Telemed.createRoomAttempts;

const getHasEverConnectedFromStore = (state) => state.Telemed.hasEverConnected;

const getTelemedChatMessagesFromStore = (state) => state.Telemed.telemedChatMessages;

const getOtherPeerEndedCallFromStore = (state) => state.Telemed.otherPeerEndedCall;

const getHandshakeFromStore = (state) => state.Telemed.handshake;

const getRemotePeerHandshakeReceivedFromStore = (state) => state.Telemed.remotePeerHandshakeReceived;

// The real professional of the consult: the appointment's `professional` (which
// may be a sub-account doctor), falling back to the master account uid.
const getTelemedProfessionalFromStore = (state) => (
  state.Telemed.telemedAppointmentData?.professional || state.Telemed.telemedMainUser
);

const dataChannelMessagesChannel = channel();

function* watchDataChannelMessagesChannel() {
  while (true) {
    const action = yield take(dataChannelMessagesChannel);
    yield put(action);
  }
}

// Bridge for dispatching from the presence notification's onClick (which runs in
// the DOM, outside a saga generator).
const telemedActionChannel = channel();

function* watchTelemedActionChannel() {
  while (true) {
    const action = yield take(telemedActionChannel);
    yield put(action);
  }
}

function validatePatientCpfOnCloudFunction({ cpf, queryConfirmedObj }) {
  return axios.post(
    `${ROOT_URL}/validatePatientCpf`,
    { cpf, queryConfirmedObj },
  );
}

export function* validatedPatientCpfRequest() {
  yield takeLatest(actions.VALIDATE_PATIENT_CPF_REQUEST, function* (action) {
    try {
      yield put({ type: actions.VALIDATING_PATIENT_CPF });
      yield call(validatePatientCpfOnCloudFunction, { ...action.payload });
      yield put({ type: actions.VALIDATE_PATIENT_CPF_SUCCESS });
    } catch (error) {
      console.warn(error);
      yield put({
        type: actions.VALIDATE_PATIENT_CPF_ERROR,
      });
    }
  });
}

function enumerateDevices() {
  return navigator.mediaDevices.enumerateDevices();
}

function getUserMedia(video = true, audio = true) {
  return navigator.mediaDevices.getUserMedia({
    video,
    audio,
  });
}

// Modal explaining how to re-enable camera/mic when permission was denied/
// blocked. Triggered by NotAllowedError — the browser-stable discriminator
// (error.message varies per browser, so we can't trust it: Safari/Firefox don't
// use Chrome's 'Permission denied').
function showPermissionDeniedModal() {
  Modal.info({
    title: 'O Meagenda não tem permissão para usar sua câmera e seu microfone',
    content: (
      <div>
        <img
          src="https://www.gstatic.com/meet/permissions_flow_meet_blocked_chrome_page_info_ltr_80ad378597e2a7cc8f65d34f0e0e7b9c.svg"
          alt="Ilustração do navegador mostrando onde corrigir as permissões bloqueadas."
          style={{
            maxWidth: '100%',
          }}
        />
        <ul
          style={{
            marginBottom: 0,
            listStyle: 'decimal',
          }}
        >
          <li>
            Acesse as informações da página no canto superior esquerdo, ao lado do endereço
            <span>
              <i style={{ fontWeight: 500 }}>&nbsp;meagenda.com.br&nbsp;</i>
            </span>
            na barra de endereço do seu navegador
          </li>
          <li>
            Ative o microfone e a câmera
          </li>
        </ul>
      </div>
    ),
    icon: null,
  });
}

// Chromium lets us query mic permission proactively (Safari/Firefox don't
// support the descriptor and throw — then we fall back to the reactive
// getUserMedia NotAllowedError path).
async function queryMicPermissionState() {
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const status = await navigator.permissions.query({ name: 'microphone' });
      return status ? status.state : null;
    }
  } catch (error) {
    // descriptor unsupported / rejected (Safari/Firefox) — continue without blocking
  }
  return null;
}

export function* openUserMedia() {
  yield takeLatest([
    actions.OPEN_USER_MEDIA_REQUEST,
    actions.OPEN_PATIENT_USER_MEDIA_REQUEST,
  ], function* (action) {
    const isPatient = action.type === 'OPEN_PATIENT_USER_MEDIA_REQUEST';
    try {
      if (action.type === 'OPEN_USER_MEDIA_REQUEST') {
        yield put({ type: actions.LOADING_OWN_WEBCAM });
      } else if (isPatient) {
        yield put({ type: actions.LOADING_PATIENT_OWN_WEBCAM });
        startPatientFunnel(action.payload || {});
      }
      // Proactive surfacing: if Chromium already reports permission denied, show
      // the "re-enable" modal without waiting for getUserMedia to fail.
      const micPermissionState = yield call(queryMicPermissionState);
      if (micPermissionState === 'denied') {
        recordGum({ granted: false, errorName: 'NotAllowedError', permissionState: 'denied' });
        if (isPatient) {
          markFunnelMedia(false, 'NotAllowedError');
          yield call(flushPatientFunnel, 'media_blocked');
        }
        showPermissionDeniedModal();
        yield put({ type: actions.OPEN_USER_MEDIA_WEBCAM_ERROR });
        return;
      }
      const availableDevices = yield call(enumerateDevices);
      const cams = availableDevices.filter((device) => device.kind === 'videoinput');
      const mics = availableDevices.filter((device) => device.kind === 'audioinput');
      const constraints = { video: cams.length > 0, audio: mics.length > 0 };
      if (cams.length === 0 && mics.length === 0) {
        // No video and audio device available
        throw new Error('No available media devices');
      }
      if (cams.length === 0) {
        // No cam
        yield put({ type: actions.NO_VIDEO_DEVICES });
      }
      if (mics.length === 0) {
        // No mic
        yield put({ type: actions.NO_AUDIO_DEVICES });
      }
      if (!localStream) {
        const stream = yield call(getUserMedia, constraints.video, constraints.audio);
        document.querySelector('#localVideo').srcObject = stream;
        if (action.type === 'OPEN_USER_MEDIA_REQUEST') {
          yield put({ type: actions.OPEN_USER_MEDIA_WEBCAM_SUCCESS });
        } else if (action.type === 'OPEN_PATIENT_USER_MEDIA_REQUEST') {
          yield put({ type: actions.OPEN_PATIENT_USER_MEDIA_WEBCAM_SUCCESS });
        }
        localStream = stream;
        // Mic sanity-check: getUserMedia resolving does NOT guarantee the mic is
        // capturing. Warn (without blocking) if a mic was expected but the track
        // is missing. The .muted/.readyState is recorded in diagnostics (can be
        // transient right after capture, so it isn't warned here).
        const audioTracks = stream.getAudioTracks();
        const videoTracks = stream.getVideoTracks();
        recordGum({
          requestedV: constraints.video,
          requestedA: constraints.audio,
          granted: true,
          audioTracks: audioTracks.length,
          videoTracks: videoTracks.length,
          audioTrackMuted: audioTracks.some((track) => track.muted),
          readyState: audioTracks[0] ? audioTracks[0].readyState : null,
          errorName: null,
        });
        if (constraints.audio && audioTracks.length === 0) {
          notification(
            'warning',
            'Microfone não detectado',
            'Sua mídia abriu, mas não encontramos um microfone ativo. Verifique o dispositivo.',
          );
        }
      }
      if (!remoteStream) {
        remoteStream = new MediaStream();
        document.querySelector('#remoteVideo').srcObject = remoteStream;
      }
      if (isPatient) {
        markFunnelMedia(true);
      }
    } catch (error) {
      console.warn(error);
      // error.name is the browser-stable discriminator (error.message varies:
      // Safari/Firefox do NOT use Chrome's 'Permission denied', so the permission
      // modal never showed on them). Legacy aliases (old Chrome) kept for safety.
      const errorName = error?.name;
      recordGum({
        granted: false,
        errorName: errorName || (error && error.message) || null,
      });
      if (isPatient) {
        markFunnelMedia(false, errorName || (error && error.message) || null);
        yield call(flushPatientFunnel, 'media_error');
      }
      if (error?.message === 'No available media devices'
        || errorName === 'NotFoundError'
        || errorName === 'DevicesNotFoundError') {
        notification('error', 'Não foi identificado nenhum dispositivo de áudio ou vídeo');
        yield put({
          type: actions.NO_MEDIA_DEVICES,
        });
      } else if (errorName === 'NotAllowedError'
        || errorName === 'PermissionDeniedError'
        || error?.message === 'Permission denied') {
        showPermissionDeniedModal();
      } else if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
        notification(
          'error',
          'Seu microfone ou câmera está em uso',
          'Feche outros programas ou abas que estejam usando a câmera/microfone (Zoom, Teams, Meet) e tente novamente.',
        );
      } else {
        notification('error', 'Não foi possível acessar sua câmera e microfone', 'Tente novamente.');
      }
      yield put({
        type: actions.OPEN_USER_MEDIA_WEBCAM_ERROR,
      });
    }
  });
}

function createRoomListener(roomId, mainUser) {
  const fs = getFirestore();
  const roomRef = doc(
    fs,
    'telemed',
    mainUser,
    'rooms',
    roomId,
  );
  const listener = eventChannel((emit) => {
    const unsubscribe = onSnapshot(roomRef, (req) => {
      emit(req || null);
    });
    return unsubscribe;
  });
  return listener;
}

function createPeerConnectionGeneralListener(mode) {
  const listener = eventChannel((emit) => {
    peerConnection.addEventListener(mode, (event) => {
      emit(event);
    });
    return () => peerConnection.removeEventListener(mode, emit);
  });
  return listener;
}

export function* createConnectionStateChange() {
  yield takeLatest(actions.REGISTER_PEER_CONNECTION_LISTENERS, function* (action) {
    try {
      const { roomId } = action.payload;
      // Watchdog: if the connection never reaches `connected` within the timeout
      // (e.g. ICE stuck in `connecting` with no relay path), force a retry instead
      // of hanging on "Aguardando..." forever. There is no native `connecting`
      // timeout in WebRTC, and only `disconnected`/`failed` were handled below.
      // Combined with the createRoomAttempts cap, this surfaces a clear error
      // after N attempts instead of an indefinite wait.
      yield fork(function* () {
        // The watchdog only tears down if the OTHER peer already joined (SDP
        // exchanged = currentRemoteDescription set). While the doctor just waits
        // for the patient, it does NOT tear down: keeping the same offer +
        // candidates alive is essential, since the patient often takes >25s to
        // join (cam/mic permission, reading the screen, clicking "Entrar"). A
        // 25s teardown used to delete the doctor's candidates, leaving late
        // patients in a dead room. The 3min deadline (telemedConnectionDeadline)
        // is the backstop for "nobody joined".
        while (true) {
          // Only retry if SDP stayed set for a WHOLE cycle without connecting, so
          // ICE always gets ~25s after the exchange (the answer often lands
          // mid-cycle on the doctor side, which would otherwise retry too early).
          const remoteDescBefore = !!(peerConnection && peerConnection.currentRemoteDescription);
          const raceResult = yield race({
            connected: take(actions.PEER_CONNECTION_CONNECTED),
            ended: take([appActions.CANCEL_LISTENERS, actions.END_TELEMED_ROOM_REQUEST]),
            timeout: call(sleep, 25000),
          });
          if (!raceResult.timeout) return;
          if (remoteDescBefore && peerConnection && peerConnection.currentRemoteDescription) {
            // SDP exchanged but stuck for a full 25s = ICE blocked → retry.
            yield put({
              type: actions.END_TELEMED_ROOM_REQUEST,
              payload: { roomId, retry: true },
            });
            return;
          }
          // No SDP yet, or answer just arrived this cycle — re-arm for a full cycle.
        }
      });
      const peerConnectionListener = yield call(createPeerConnectionGeneralListener, 'connectionstatechange');
      yield takeEvery(peerConnectionListener, function* () {
        if (!peerConnection) return;
        if (peerConnection.connectionState === 'failed') {
          yield put({
            type: actions.END_TELEMED_ROOM_REQUEST,
            payload: { roomId, retry: true },
          });
        } else if (peerConnection.connectionState === 'disconnected') {
          // 'disconnected' is transient per WebRTC spec — ICE may recover on its
          // own. Waiting before tearing down avoids dropping calls under
          // momentary network blips.
          yield call(sleep, 8000);
          if (peerConnection
            && (peerConnection.connectionState === 'disconnected'
              || peerConnection.connectionState === 'failed')) {
            yield put({
              type: actions.END_TELEMED_ROOM_REQUEST,
              payload: { roomId, retry: true },
            });
          }
        } else if (peerConnection.connectionState === 'connected') {
          yield put({
            type: actions.PEER_CONNECTION_CONNECTED,
          });
          // Telemetry (doctor side only): count each consult once, and flag if it
          // needed the (paid) TURN relay. Best-effort — never break the call.
          try {
            const userType = yield select(getUserTypeFromStore);
            if (userType === 'professional') {
              const professionalId = yield select(getTelemedProfessionalFromStore);
              if (!telemedConsultCounted) {
                telemedConsultCounted = true;
                yield call(incrementTelemedStatRequest, 'connected', professionalId);
              }
              if (!telemedTurnCounted) {
                const usedTurn = yield call(didConnectionUseTurn, peerConnection);
                if (usedTurn) {
                  telemedTurnCounted = true;
                  yield call(incrementTelemedStatRequest, 'turn', professionalId);
                }
              }
            }
          } catch (statError) {
            console.warn('telemed stat error', statError);
          }
        }
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      peerConnectionListener.close();
    } catch (error) {
      console.warn(error);
    }
  });
}

export function* createDataChannel() {
  yield takeLatest(actions.REGISTER_PEER_CONNECTION_LISTENERS, function* () {
    try {
      const peerConnectionListener = yield call(createPeerConnectionGeneralListener, 'datachannel');
      yield takeEvery(peerConnectionListener, function* (event) {
        dataChannel = event.channel;
        yield put({
          type: actions.CREATE_DATA_CHANNEL_SUCCESS,
        });
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      peerConnectionListener.close();
    } catch (error) {
      console.warn(error);
    }
  });
}

function createDataChannelGeneralListener(mode) {
  const listener = eventChannel((emit) => {
    dataChannel.addEventListener(mode, (event) => {
      emit(event);
    });
    return () => dataChannel.removeEventListener(mode, emit);
  });
  return listener;
}

export function* createDataChannelOpenStateChange() {
  yield takeLatest(actions.REGISTER_PEER_CONNECTION_LISTENERS, function* () {
    try {
      const dataChannelListener = yield call(createDataChannelGeneralListener, 'open');
      yield takeEvery(dataChannelListener, function* () {
        yield put({
          type: actions.DATA_CHANNEL_STATE_IS_OPEN,
        });
        const ownMicMuted = yield select(getOwnMicMutedFromStore);
        const userType = yield select(getUserTypeFromStore);
        const noVideoDevices = yield select(getNoVideoDevicesFromStore);
        const noAudioDevices = yield select(getNoAudioDevicesFromStore);
        const webcamOff = userType === 'patient'
          ? yield select(getUserPatientMediaWebcamDisabledFromStore)
          : yield select(getUserMediaWebcamDisabledFromStore);
        if (dataChannel && dataChannel.readyState === 'open') {
          // Send handshake before sending initial states
          let handshake = yield select(getHandshakeFromStore);
          let remotePeerHandshakeReceived = yield select(getRemotePeerHandshakeReceivedFromStore);
          try {
            while (dataChannel && (!handshake || !remotePeerHandshakeReceived)) {
              dataChannel.send(JSON.stringify({ type: 'handshake' }));
              yield call(sleep, 500);
              if (!dataChannel) break;
              handshake = yield select(getHandshakeFromStore);
              remotePeerHandshakeReceived = yield select(getRemotePeerHandshakeReceivedFromStore);
            }
            if (!dataChannel) return;
            if (ownMicMuted) {
              dataChannel.send(JSON.stringify({ type: 'micMuted' }));
            } else {
              dataChannel.send(JSON.stringify({ type: 'micUnmuted' }));
            }
            if (webcamOff) {
              dataChannel.send(JSON.stringify({ type: 'camOff' }));
            } else {
              dataChannel.send(JSON.stringify({ type: 'camOn' }));
            }
            if (noVideoDevices) {
              dataChannel.send(JSON.stringify({ type: 'camOff' }));
            }
            if (noAudioDevices) {
              dataChannel.send(JSON.stringify({ type: 'micMuted' }));
            }
          } catch (e) {
            console.warn(e);
          }
        }
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      dataChannelListener.close();
    } catch (error) {
      console.warn(error);
    }
  });
}

export function* createDataChannelCloseStateChange() {
  yield takeLatest(actions.REGISTER_PEER_CONNECTION_LISTENERS, function* () {
    try {
      const dataChannelListener = yield call(createDataChannelGeneralListener, 'close');
      yield takeEvery(dataChannelListener, function* () {
        yield put({
          type: actions.DATA_CHANNEL_STATE_IS_CLOSE,
        });
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      dataChannelListener.close();
    } catch (error) {
      console.warn(error);
    }
  });
}

export function* createDataChannelMessageListener() {
  yield takeLatest(actions.REGISTER_PEER_CONNECTION_LISTENERS, function* () {
    try {
      const dataChannelListener = yield call(createDataChannelGeneralListener, 'message');
      yield takeEvery(dataChannelListener, function* (event) {
        const message = JSON.parse(event.data);
        if (message?.type === 'handshake') {
          yield put({
            type: actions.HANDSHAKE_RECEIVED,
          });
          dataChannel.send(JSON.stringify({ type: 'remotePeerHandshakeReceived' }));
        }
        if (message?.type === 'remotePeerHandshakeReceived') {
          yield put({
            type: actions.REMOTE_PEER_HANDSHAKE_RECEIVED,
          });
        }
        if (message?.type === 'micMuted') {
          yield put({
            type: actions.REMOTE_MIC_MUTED,
          });
        }
        if (message?.type === 'micUnmuted') {
          yield put({
            type: actions.REMOTE_MIC_UNMUTED,
          });
        }
        if (message?.type === 'camOff') {
          yield put({
            type: actions.REMOTE_CAM_OFF,
          });
        }
        if (message?.type === 'camOn') {
          yield put({
            type: actions.REMOTE_CAM_ON,
          });
        }
        if (message?.type === 'chatMessage') {
          yield put({
            type: actions.RECEIVED_CHAT_MESSAGE,
            payload: { ...message.data },
          });
        }
        if (message?.type === 'endCall') {
          yield put({
            type: actions.OTHER_PEER_ENDED_CALL,
          });
        }
      });
      const actionTriggered = yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      if (actionTriggered?.type === 'END_TELEMED_ROOM_REQUEST' && !actionTriggered.payload?.retry) {
        const dataChannelOpen = yield select(getDataChannelOpenFromStore);
        if (dataChannel && dataChannelOpen) {
          dataChannel.send(JSON.stringify({ type: 'endCall' }));
        }
      }
      dataChannelListener.close();
    } catch (error) {
      console.warn(error);
    }
  });
}

function createOffer() {
  const offerOptions = {
    offerToReceiveAudio: true,
    offerToReceiveVideo: true, // Force video receiving
  };
  return peerConnection.createOffer(offerOptions);
}

function setLocalDescription(offer) {
  return peerConnection.setLocalDescription(offer);
}

function deleteRoom(roomId, mainUser) {
  const fs = getFirestore();
  const roomRef = doc(
    fs,
    'telemed',
    mainUser,
    'rooms',
    roomId,
  );
  return deleteDoc(roomRef);
}

function createRoomWithOfferOnFirestore(roomId, mainUser, offer, sessionUuid) {
  const fs = getFirestore();
  const roomRef = doc(
    fs,
    'telemed',
    mainUser,
    'rooms',
    roomId,
  );
  const roomWithOffer = {
    offer: {
      type: offer.type,
      sdp: offer.sdp,
    },
    sessionUuid,
    // Lets the patient tell a live offer from a zombie room. See STALE_OFFER_MS.
    createdAt: serverTimestamp(),
  };
  return setDoc(roomRef, roomWithOffer);
}

export function* createRoom() {
  yield takeLatest(actions.CREATE_TELEMED_ROOM_REQUEST, function* (action) {
    try {
      const mainUser = yield select(getMainUserFromStore);
      let uid;
      if (mainUser) {
        uid = mainUser;
      } else {
        const auth = getAuth();
        const { currentUser } = auth;
        ({ uid } = currentUser);
      }
      yield put({
        type: actions.SET_TELEMED_MAIN_USER,
        payload: uid,
      });
      const { roomId } = action.payload;
      const createRoomAttempts = yield select(getCreateRoomAttemptsFromStore);
      // No attempt-based giveup here: the time-based deadline
      // (telemedConnectionDeadline) is the single authority that stops retrying
      // and surfaces the manual "Tentar novamente" button after a couple minutes.
      // The counter is kept only to pick the progressive notification message.
      yield put({
        type: actions.CREATING_TELEMED_ROOM,
        payload: {
          // counter: newCreateRoomAttempts,
          counter: createRoomAttempts + 1,
        },
      });
      const roomListener = yield call(createRoomListener, roomId, uid);
      const sessionUuid = uuidv1();
      yield takeEvery(roomListener, function* (snapshot) {
        const currentSessionUuid = yield select(getSessionUuidFromStore);
        if (!snapshot.exists()) {
          if (currentSessionUuid) {
            // Need to clean and start again
            yield put({
              type: actions.END_TELEMED_ROOM_REQUEST,
              payload: { roomId, retry: true },
            });
          } else {
            // No room on DB and states are reset
            const pcConfiguration = yield call(buildPeerConnectionConfiguration, roomId, uid);
            peerConnection = new RTCPeerConnection(pcConfiguration);
            if (process.env.NODE_ENV === 'development') {
              window.telemedPC = peerConnection;
            }
            pendingRemoteCandidates = [];
            const professionalId = yield select(getTelemedProfessionalFromStore);
            startDiagnostics(peerConnection, { role: 'professional', roomId, professionalId });
            dataChannel = peerConnection.createDataChannel(roomId);
            yield put({
              type: actions.REGISTER_PEER_CONNECTION_LISTENERS,
              payload: { roomId },
            });
            localStream.getTracks().forEach((track) => {
              peerConnection.addTrack(track, localStream);
            });
            yield put({
              type: actions.CREATE_PEER_CONNECTION_SUCCESS,
              payload: {
                roomId,
              },
            });
            // Code for creating a room
            const offer = yield call(createOffer);
            yield call(setLocalDescription, offer);
            yield call(createRoomWithOfferOnFirestore, roomId, uid, offer, sessionUuid);
            yield put({
              type: actions.SET_SESSION_UUID,
              payload: {
                sessionUuid,
              },
            });
            // Progressive feedback: the message escalates with the attempt count
            // so the user understands what is happening during the 2-minute window.
            const hasEverConnected = yield select(getHasEverConnectedFromStore);
            if (createRoomAttempts === 0 && !hasEverConnected) {
              notification(
                'success',
                'Sala criada com sucesso',
                'Aguardando o paciente se conectar.',
              );
            } else if (createRoomAttempts <= 1) {
              notification(
                'info',
                'Reconectando',
                'Restabelecendo a conexão com o paciente.',
              );
            } else if (createRoomAttempts <= 3) {
              notification(
                'info',
                'Testando a conexão',
                'A conexão está instável. Tentando outras formas de conectar...',
              );
            } else {
              notification(
                'warning',
                'Ainda tentando conectar',
                'A rede parece instável, aguarde mais um instante...',
              );
            }
            // Code for creating a room above
          }
        } else if (snapshot.exists()) {
          const { sessionUuid: sessionUuidFirestore } = snapshot.data();
          if (sessionUuidFirestore === currentSessionUuid || sessionUuidFirestore === sessionUuid) {
            // It is the current room, no action required
          } else if (!currentSessionUuid) {
            yield put({
              type: actions.END_TELEMED_ROOM_REQUEST,
              payload: { roomId, retry: true },
            });
          }
        }
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      roomListener.close();
    } catch (error) {
      console.warn(error);
      notification('error', 'Algo deu errado ao tentar criar uma sala', 'Tente novamente mais tarde.');
    }
  });
}

function createPeerConnectionCandidateListener(mode) {
  // Code for collecting ICE candidates
  const listener = eventChannel((emit) => {
    peerConnection.addEventListener(mode, (event) => {
      if (!event.candidate) {
        return;
      }
      emit(event.candidate.toJSON());
    });
    return () => peerConnection.removeEventListener(mode, emit);
  });
  return listener;
}

function addCallerCandidateOnFirestore(roomId, mainUser, eventCandidate) {
  const fs = getFirestore();
  const callerCandidatesCollection = collection(
    fs,
    'telemed',
    mainUser,
    'rooms',
    roomId,
    'callerCandidates',
  );
  return addDoc(callerCandidatesCollection, eventCandidate);
}

export function* getCallerEventCandidates() {
  yield takeLatest(actions.CREATE_PEER_CONNECTION_SUCCESS, function* (action) {
    try {
      const { roomId } = action.payload;
      const telemedMainUser = yield select(getTelemedMainUserFromStore);
      const peerConnectionCandidateListener = yield call(createPeerConnectionCandidateListener, 'icecandidate');
      yield takeEvery(peerConnectionCandidateListener, function* (eventCandidate) {
        // Getting ice candidates from eventListener and saving to firestore
        yield call(addCallerCandidateOnFirestore, roomId, telemedMainUser, eventCandidate);
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      peerConnectionCandidateListener.close();
    } catch (error) {
      console.warn(error);
      notification('error', 'Algo deu errado ao tentar se comunicar com o outro usuário', 'Tente novamente mais tarde.');
      yield put({
        type: actions.GET_EVENT_CANDIDATES_ERROR,
      });
    }
  });
}

function createPeerConnectionTrackListener() {
  const listener = eventChannel((emit) => {
    const handler = (event) => emit(event);
    peerConnection.addEventListener('track', handler);
    // Bugfix: o cleanup antigo removia `emit` (handler errado) em vez do listener
    // realmente registrado, deixando o handler vazado entre chamadas.
    return () => peerConnection.removeEventListener('track', handler);
  });
  return listener;
}

// Ensures remote video/audio playback. On Safari/iOS autoplay with audio is
// blocked outside a gesture -> on NotAllowedError we play muted (video shows)
// and signal the UI to ask for a tap (ENABLE_REMOTE_AUDIO_REQUEST).
function* tryPlayRemoteVideo() {
  const el = document.querySelector('#remoteVideo');
  if (!el) return;
  try {
    el.muted = false;
    yield call(playMediaElement, el);
    yield put({ type: actions.REMOTE_AUDIO_AUTOPLAY_OK });
  } catch (error) {
    if (error?.name === 'NotAllowedError') {
      try {
        el.muted = true;
        yield call(playMediaElement, el);
      } catch (mutedError) {
        console.warn('remote video muted play failed', mutedError);
      }
      yield put({ type: actions.REMOTE_AUDIO_AUTOPLAY_BLOCKED });
    } else {
      console.warn('remote video play failed', error);
    }
  }
}

export function* getRemoteTrack() {
  yield takeLatest([
    actions.CREATE_PEER_CONNECTION_SUCCESS,
    actions.JOINING_TELEMED_CREATE_PEER_CONNECTION_SUCCESS,
  ], function* () {
    try {
      const peerConnectionTrackListener = yield call(createPeerConnectionTrackListener);
      yield takeEvery(peerConnectionTrackListener, function* (event) {
        if (remoteStream && event?.streams?.[0]) {
          event.streams[0].getTracks().forEach((track) => {
            remoteStream.addTrack(track);
          });
        }
        yield call(tryPlayRemoteVideo);
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      peerConnectionTrackListener.close();
    } catch (error) {
      console.warn(error);
      yield put({
        type: actions.GET_REMOTE_TRACK_ERROR,
      });
    }
  });
}

// User gesture (tap on the overlay) that unlocks remote audio: unmutes, plays
// again and resumes the AudioContext (which starts 'suspended' on Safari).
export function* enableRemoteAudio() {
  yield takeLatest(actions.ENABLE_REMOTE_AUDIO_REQUEST, function* () {
    try {
      getSharedAudioContext();
      yield call(resumeSharedAudioContext);
      const el = document.querySelector('#remoteVideo');
      if (el) {
        el.muted = false;
        yield call(playMediaElement, el);
      }
      yield put({ type: actions.REMOTE_AUDIO_AUTOPLAY_OK });
    } catch (error) {
      console.warn('enable remote audio failed', error);
    }
  });
}

function createSessionDescriptionListener(roomId, mainUser) {
  const fs = getFirestore();
  const roomRef = doc(
    fs,
    'telemed',
    mainUser,
    'rooms',
    roomId,
  );
  const listener = eventChannel((emit) => {
    const unsubscribe = onSnapshot(roomRef, (req) => {
      emit(req || {});
    });
    return unsubscribe;
  });
  return listener;
}

function setRemoteDescription(rtcSessionDescription) {
  return peerConnection.setRemoteDescription(rtcSessionDescription);
}

function addIceCandidate(data) {
  if (!peerConnection) return Promise.resolve();
  // Buffer while remoteDescription isn't set yet (avoids Safari/spec
  // InvalidStateError); the flush below drains them later.
  if (!peerConnection.remoteDescription || !peerConnection.remoteDescription.type) {
    pendingRemoteCandidates.push(data);
    return Promise.resolve();
  }
  return peerConnection.addIceCandidate(new RTCIceCandidate(data));
}

function flushPendingRemoteCandidates() {
  if (!peerConnection || !peerConnection.remoteDescription) return Promise.resolve();
  const pending = pendingRemoteCandidates;
  pendingRemoteCandidates = [];
  return Promise.all(pending.map((data) => (
    peerConnection.addIceCandidate(new RTCIceCandidate(data)).catch((error) => {
      console.warn('flushPendingRemoteCandidates failed', error);
    })
  )));
}

export function* getSessionDescription() {
  yield takeLatest(actions.CREATE_PEER_CONNECTION_SUCCESS, function* (action) {
    try {
      const { roomId } = action.payload;
      const telemedMainUser = yield select(getTelemedMainUserFromStore);
      const sessionDescriptionListener = yield call(createSessionDescriptionListener, roomId, telemedMainUser);
      yield takeEvery(sessionDescriptionListener, function* (snapshot) {
        const data = snapshot.data();
        if (!data?.answer || !peerConnection) return;
        const currentSdp = peerConnection.currentRemoteDescription?.sdp;
        if (!currentSdp) {
          const rtcSessionDescription = new RTCSessionDescription(data.answer);
          yield call(setRemoteDescription, rtcSessionDescription);
          yield call(flushPendingRemoteCandidates);
        } else if (currentSdp !== data.answer.sdp) {
          // Answer SDP changed = patient reconnected (e.g. F5) and wrote a new
          // answer. Without this we'd keep a zombie peer until connectionState
          // turns 'disconnected' — which can take minutes on symmetric-NAT /
          // no-TURN networks.
          yield put({
            type: actions.END_TELEMED_ROOM_REQUEST,
            payload: { roomId, retry: true },
          });
        }
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      sessionDescriptionListener.close();
    } catch (error) {
      console.warn(error);
      yield put({
        type: actions.GET_SESSION_DESCRIPTION_ERROR,
      });
    }
  });
}

function createIceCalleeCandidatesListener(roomId, mainUser) {
  const fs = getFirestore();
  const calleeCandidatesCollection = collection(
    fs,
    'telemed',
    mainUser,
    'rooms',
    roomId,
    'calleeCandidates',
  );
  const listener = eventChannel((emit) => {
    const unsubscribe = onSnapshot(calleeCandidatesCollection, (req) => {
      emit(req || {});
    });
    return unsubscribe;
  });
  return listener;
}

export function* getCalleeRemoteCandidates() {
  yield takeLatest(actions.CREATE_PEER_CONNECTION_SUCCESS, function* (action) {
    try {
      const { roomId } = action.payload;
      const telemedMainUser = yield select(getTelemedMainUserFromStore);
      const iceCandidatesListener = yield call(createIceCalleeCandidatesListener, roomId, telemedMainUser);
      yield takeEvery(iceCandidatesListener, function* (snapshot) {
        const iceCandidatesArr = [];
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const data = change.doc.data();
            iceCandidatesArr.push(data);
          }
        });
        try {
          yield all(iceCandidatesArr.map((data) => call(addIceCandidate, data)));
        } catch (e) {
          console.warn(e);
        }
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      iceCandidatesListener.close();
    } catch (error) {
      console.warn(error);
      yield put({
        type: actions.GET_REMOTE_ICE_CANDIDATES_ERROR,
      });
    }
  });
}

function createAnswer() {
  return peerConnection.createAnswer();
}

function updateRoomWithAnswerOnFirestore(roomId, mainUser, answer) {
  const fs = getFirestore();
  const roomRef = doc(
    fs,
    'telemed',
    mainUser,
    'rooms',
    roomId,
  );
  const roomWithAnswer = {
    answer: {
      type: answer.type,
      sdp: answer.sdp,
    },
  };
  return updateDoc(roomRef, roomWithAnswer);
}

export function* joinRoomById() {
  yield takeLatest(actions.JOIN_TELEMED_ROOM_REQUEST, function* (action) {
    try {
      yield put({ type: actions.JOINING_TELEMED_ROOM });
      const { roomId, mainUser } = action.payload;
      markFunnelEntered();
      yield put({
        type: actions.SET_TELEMED_MAIN_USER,
        payload: mainUser,
      });
      // Signal presence so the doctor sees the patient in the waiting room.
      // Best-effort — must not break joining the room.
      try {
        yield call(setTelemedPresenceRequest, { mainUser, roomId, status: 'waiting' });
      } catch (presenceError) {
        console.warn('setTelemedPresence (waiting) failed', presenceError);
      }
      const roomListener = yield call(createRoomListener, roomId, mainUser);
      yield takeEvery(roomListener, function* (snapshot) {
        if (!snapshot.exists()) {
          // No room yet: stay in "waiting for doctor" (no PC, no deadline) with the
          // listener alive — we auto-connect when the offer appears.
          yield put({ type: actions.TELEMED_WAITING_FOR_DOCTOR });
          return;
        }
        // Guard: writing our own `answer` re-fires the snapshot; without this we'd
        // build a second (zombie) peerConnection over the first.
        if (peerConnection) return;
        const { offer, sessionUuid, createdAt } = snapshot.data();
        // Zombie room (stale doc): an offer past STALE_OFFER_MS (or incomplete) is a
        // ghost → keep waiting for the doctor's new offer. Missing createdAt (pending
        // serverTimestamp) is treated as live (connect).
        const createdAtMs = createdAt && createdAt.toMillis ? createdAt.toMillis() : null;
        const isStaleOffer = createdAtMs !== null && (Date.now() - createdAtMs > STALE_OFFER_MS);
        if (!offer || isStaleOffer) {
          yield put({ type: actions.TELEMED_WAITING_FOR_DOCTOR });
          return;
        }
        const pcConfiguration = yield call(buildPeerConnectionConfiguration, roomId, mainUser);
        peerConnection = new RTCPeerConnection(pcConfiguration);
        if (process.env.NODE_ENV === 'development') {
          window.telemedPC = peerConnection;
        }
        pendingRemoteCandidates = [];
        const professionalId = yield select(getTelemedProfessionalFromStore);
        startDiagnostics(peerConnection, { role: 'patient', roomId, professionalId });
        markFunnelReachedWebrtc();
        dataChannel = peerConnection.createDataChannel(roomId);
        yield put({
          type: actions.REGISTER_PEER_CONNECTION_LISTENERS,
          payload: { roomId },
        });
        localStream.getTracks().forEach((track) => {
          peerConnection.addTrack(track, localStream);
        });
        yield put({
          type: actions.JOINING_TELEMED_CREATE_PEER_CONNECTION_SUCCESS,
          payload: {
            roomId,
            mainUser,
          },
        });

        // Code for creating SDP answer below
        const rtcSessionDescription = new RTCSessionDescription(offer);
        yield call(setRemoteDescription, rtcSessionDescription);
        yield call(flushPendingRemoteCandidates);
        const answer = yield call(createAnswer);
        yield call(setLocalDescription, answer);
        yield call(updateRoomWithAnswerOnFirestore, roomId, mainUser, answer);
        yield put({
          type: actions.JOIN_TELEMED_ROOM_SUCCESS,
          payload: {
            roomId,
            mainUser,
            sessionUuid,
          },
        });
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
        actions.JOINING_TELEMED_CREATE_PEER_CONNECTION_SUCCESS,
      ]);
      roomListener.close();
    } catch (error) {
      console.warn(error);
      notification('error', 'Algo deu errado ao tentar entrar na sala', 'Tente novamente mais tarde.');
    }
  });
}

export function* checkRoomSessionUuid() {
  yield takeLatest(actions.JOIN_TELEMED_ROOM_SUCCESS, function* (action) {
    try {
      yield put({ type: actions.JOINING_TELEMED_ROOM });
      const { roomId, mainUser, sessionUuid: currentSessionUuid } = action.payload;
      const roomListener = yield call(createRoomListener, roomId, mainUser);
      yield takeEvery(roomListener, function* (snapshot) {
        if (snapshot.exists()) {
          // Code for creating SDP answer below
          const { sessionUuid } = snapshot.data();
          if ((currentSessionUuid && currentSessionUuid !== sessionUuid) || !sessionUuid) {
            // The caller already have a different 'sessionUuid', need to retry.
            yield put({
              type: actions.END_TELEMED_ROOM_REQUEST,
              payload: { roomId, retry: true },
            });
          }
        } else {
          yield put({
            type: actions.END_TELEMED_ROOM_REQUEST,
            payload: { roomId, retry: true },
          });
        }
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      roomListener.close();
    } catch (error) {
      console.warn(error);
      notification('error', 'Algo deu errado ao tentar entrar na sala', 'Tente novamente mais tarde.');
    }
  });
}

function addCalleeCandidateOnFirestore(roomId, mainUser, eventCandidate) {
  const fs = getFirestore();
  const calleeCandidatesCollection = collection(
    fs,
    'telemed',
    mainUser,
    'rooms',
    roomId,
    'calleeCandidates',
  );
  return addDoc(calleeCandidatesCollection, eventCandidate);
}

export function* getCalleeEventCandidates() {
  yield takeLatest(actions.JOINING_TELEMED_CREATE_PEER_CONNECTION_SUCCESS, function* (action) {
    try {
      const { roomId } = action.payload;
      const telemedMainUser = yield select(getTelemedMainUserFromStore);
      const peerConnectionCandidateListener = yield call(createPeerConnectionCandidateListener, 'icecandidate');
      yield takeEvery(peerConnectionCandidateListener, function* (eventCandidate) {
        // Getting ice candidates from eventListener and saving to firestore
        yield call(addCalleeCandidateOnFirestore, roomId, telemedMainUser, eventCandidate);
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      peerConnectionCandidateListener.close();
    } catch (error) {
      console.warn(error);
      yield put({
        type: actions.GET_EVENT_CANDIDATES_ERROR,
      });
    }
  });
}

function createIceCallerCandidatesListener(roomId, mainUser) {
  const fs = getFirestore();
  const callerCandidatesCollection = collection(
    fs,
    'telemed',
    mainUser,
    'rooms',
    roomId,
    'callerCandidates',
  );
  const listener = eventChannel((emit) => {
    const unsubscribe = onSnapshot(callerCandidatesCollection, (req) => {
      emit(req || {});
    });
    return unsubscribe;
  });
  return listener;
}

export function* getCallerRemoteCandidates() {
  yield takeLatest(actions.JOINING_TELEMED_CREATE_PEER_CONNECTION_SUCCESS, function* (action) {
    try {
      const { roomId } = action.payload;
      const telemedMainUser = yield select(getTelemedMainUserFromStore);
      const iceCandidatesListener = yield call(createIceCallerCandidatesListener, roomId, telemedMainUser);
      yield takeEvery(iceCandidatesListener, function* (snapshot) {
        const iceCandidatesArr = [];
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const data = change.doc.data();
            iceCandidatesArr.push(data);
          }
        });
        // Inner try/catch: one rejected addIceCandidate must not kill the whole
        // listener (and lose later candidates). The buffer already avoids the
        // InvalidStateError when they arrive before setRemoteDescription.
        try {
          yield all(iceCandidatesArr.map((data) => call(addIceCandidate, data)));
        } catch (e) {
          console.warn(e);
        }
      });
      yield take([
        appActions.CANCEL_LISTENERS,
        actions.END_TELEMED_ROOM_REQUEST,
      ]);
      iceCandidatesListener.close();
    } catch (error) {
      console.warn(error);
      yield put({
        type: actions.GET_REMOTE_ICE_CANDIDATES_ERROR,
      });
    }
  });
}

// Reads local tracks resiliently from the module `localStream` (falls back to
// #localVideo just in case), avoiding the `.srcObject` null-deref when the
// element hasn't mounted or media capture failed.
function getLocalTracks(kind) {
  let stream = localStream;
  if (!stream) {
    const el = document.querySelector('#localVideo');
    stream = el ? el.srcObject : null;
  }
  if (!stream) return [];
  return kind === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks();
}

export function* stopOwnWebcam() {
  yield takeLatest(actions.STOP_OWN_WEBCAM_REQUEST, function* () {
    try {
      const videoTracks = getLocalTracks('video');
      if (!videoTracks.length) {
        console.warn('stopOwnWebcam: nenhum track de vídeo disponível');
        return;
      }
      videoTracks.forEach((track) => {
        if (track?.enabled) {
          // eslint-disable-next-line no-param-reassign
          track.enabled = false;
        }
      });
      const userType = yield select(getUserTypeFromStore);
      const dataChannelOpen = yield select(getDataChannelOpenFromStore);
      if (dataChannel && dataChannelOpen) {
        dataChannel.send(JSON.stringify({ type: 'camOff' }));
      }
      if (userType === 'patient') {
        yield put({
          type: actions.STOP_OWN_PATIENT_WEBCAM_SUCCESS,
        });
      } else {
        yield put({
          type: actions.STOP_OWN_WEBCAM_SUCCESS,
        });
      }
    } catch (error) {
      console.warn(error);
    }
  });
}

export function* startOwnWebcam() {
  yield takeLatest(actions.START_OWN_WEBCAM_REQUEST, function* () {
    try {
      const videoTracks = getLocalTracks('video');
      if (!videoTracks.length) {
        console.warn('startOwnWebcam: nenhum track de vídeo disponível');
        return;
      }
      videoTracks.forEach((track) => {
        if (!track?.enabled) {
          // eslint-disable-next-line no-param-reassign
          track.enabled = true;
        }
      });
      const userType = yield select(getUserTypeFromStore);
      const dataChannelOpen = yield select(getDataChannelOpenFromStore);
      if (dataChannel && dataChannelOpen) {
        dataChannel.send(JSON.stringify({ type: 'camOn' }));
      }
      if (userType === 'patient') {
        yield put({
          type: actions.START_OWN_PATIENT_WEBCAM_SUCCESS,
        });
      } else {
        yield put({
          type: actions.START_OWN_WEBCAM_SUCCESS,
        });
      }
    } catch (error) {
      console.warn(error);
    }
  });
}

export function* muteOwnMic() {
  yield takeLatest(actions.MUTE_OWN_MIC_REQUEST, function* () {
    try {
      const audioTracks = getLocalTracks('audio');
      if (!audioTracks.length) {
        console.warn('muteOwnMic: nenhum track de áudio disponível');
        return;
      }
      audioTracks.forEach((track) => {
        if (track?.enabled) {
          // eslint-disable-next-line no-param-reassign
          track.enabled = false;
        }
      });
      const dataChannelOpen = yield select(getDataChannelOpenFromStore);
      if (dataChannel && dataChannelOpen) {
        dataChannel.send(JSON.stringify({ type: 'micMuted' }));
      }
      yield put({
        type: actions.MUTE_OWN_MIC_SUCCESS,
      });
    } catch (error) {
      console.warn(error);
    }
  });
}

export function* unmuteOwnMic() {
  yield takeLatest(actions.UNMUTE_OWN_MIC_REQUEST, function* () {
    try {
      const audioTracks = getLocalTracks('audio');
      if (!audioTracks.length) {
        console.warn('unmuteOwnMic: nenhum track de áudio disponível');
        return;
      }
      audioTracks.forEach((track) => {
        if (!track?.enabled) {
          // eslint-disable-next-line no-param-reassign
          track.enabled = true;
        }
      });
      const dataChannelOpen = yield select(getDataChannelOpenFromStore);
      if (dataChannel && dataChannelOpen) {
        dataChannel.send(JSON.stringify({ type: 'micUnmuted' }));
      }
      yield put({
        type: actions.UNMUTE_OWN_MIC_SUCCESS,
      });
    } catch (error) {
      console.warn(error);
    }
  });
}

export function* sendChatMessage() {
  yield takeLatest(actions.SEND_CHAT_MESSAGE, function* (action) {
    try {
      const dataChannelOpen = yield select(getDataChannelOpenFromStore);
      if (dataChannel && dataChannelOpen) {
        const chatMessages = yield select(getTelemedChatMessagesFromStore);
        const data = {
          ...action.payload,
          id: uuidv1(),
          sendingNewMessage: true,
          timestamp: moment().tz('America/Sao_Paulo').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
        };
        yield put({
          type: actions.SAVE_CHAT_MESSAGES,
          payload: {
            messages: _.uniqBy([data, ...chatMessages], 'id'),
          },
        });
        dataChannel.send(JSON.stringify({
          type: 'chatMessage',
          data,
        }));
      }
    } catch (error) {
      console.warn(error);
    }
  });
}

export function* receivedChatMessage() {
  yield takeLatest(actions.RECEIVED_CHAT_MESSAGE, function* (action) {
    try {
      const chatMessages = yield select(getTelemedChatMessagesFromStore);
      const data = {
        ...action.payload,
        timestamp: moment().tz('America/Sao_Paulo').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      };
      if (data.sendingNewMessage) {
        delete data.sendingNewMessage;
        dataChannel.send(JSON.stringify({
          type: 'chatMessage',
          data,
        }));
      }
      yield put({
        type: actions.SAVE_CHAT_MESSAGES,
        payload: {
          messages: _.uniqBy([data, ...chatMessages], 'id'),
        },
      });
    } catch (error) {
      console.warn(error);
    }
  });
}

function getCalleeCandidatesFromFirestore(roomId, mainUser) {
  const fs = getFirestore();
  const calleeCandidatesCollection = collection(
    fs,
    'telemed',
    mainUser,
    'rooms',
    roomId,
    'calleeCandidates',
  );
  return getDocs(calleeCandidatesCollection);
}

function deleteCandidate(candidate) {
  return deleteDoc(candidate.ref);
}

function getCallerCandidatesFromFirestore(roomId, mainUser) {
  const fs = getFirestore();
  const callerCandidatesCollection = collection(
    fs,
    'telemed',
    mainUser,
    'rooms',
    roomId,
    'callerCandidates',
  );
  return getDocs(callerCandidatesCollection);
}

export function* hangUp() {
  yield takeLatest(actions.END_TELEMED_ROOM_REQUEST, function* (action) {
    try {
      const { roomId, retry, timedOut } = action.payload;
      yield put({
        type: actions.ENDING_TELEMED_ROOM,
        // `timedOut` is a soft stop, not a real "ending" — reuse 'retry' so the
        // UI doesn't flash "Encerrando a chamada".
        payload: (retry || timedOut) ? 'retry' : true,
      });
      let shouldRetry = retry;
      const otherPeerEndedCall = yield select(getOtherPeerEndedCallFromStore);
      if (otherPeerEndedCall) {
        shouldRetry = false;
      }
      // Diagnostics: flush the record BEFORE closing the peerConnection (the
      // flush takes a final getStats sample, which needs the PC still open).
      try {
        if (timedOut) {
          yield call(flushDiagnostics, 'timed_out');
        } else if (shouldRetry) {
          yield call(flushDiagnostics, 'failed');
        } else {
          const everConnected = yield select(getHasEverConnectedFromStore);
          yield call(flushDiagnostics, everConnected ? 'ended' : 'abandoned');
        }
      } catch (diagError) {
        console.warn('telemed diagnostics flush failed', diagError);
      }
      // Patient funnel: only persists if they did NOT reach WebRTC (on the
      // doctor the funnel is null → no-op). Catches "joined but the room/offer
      // never appeared".
      try {
        let funnelReason = 'ended';
        if (timedOut) funnelReason = 'timed_out';
        else if (shouldRetry) funnelReason = 'retry';
        yield call(flushPatientFunnel, funnelReason);
      } catch (funnelError) {
        console.warn('telemed patient funnel flush failed', funnelError);
      }
      // Clear presence when the patient really leaves (retry/timedOut keep waiting;
      // the doctor also clears on connect, and staleness covers abandoned tabs).
      if (!shouldRetry && !timedOut && roomId) {
        const endUserType = yield select(getUserTypeFromStore);
        const endMainUser = yield select(getTelemedMainUserFromStore);
        if (endUserType === 'patient' && endMainUser) {
          try {
            yield call(setTelemedPresenceRequest, {
              mainUser: endMainUser, roomId, status: 'gone',
            });
          } catch (presenceError) {
            console.warn('setTelemedPresence (gone) failed', presenceError);
          }
        }
      }
      if (!shouldRetry && !timedOut) {
        // Use the module `localStream` (resilient to a null #localVideo.srcObject
        // when capture never opened) instead of reading straight from the DOM.
        const localEl = document.querySelector('#localVideo');
        const localStreamRef = localStream || (localEl ? localEl.srcObject : null);
        if (localStreamRef) {
          localStreamRef.getTracks().forEach((track) => {
            track.stop();
          });
        }
        localStream = null;
      }
      if (remoteStream) {
        remoteStream.getTracks().forEach((track) => track.stop());
        if (shouldRetry || timedOut) {
          remoteStream = new MediaStream();
          document.querySelector('#remoteVideo').srcObject = remoteStream;
        } else {
          remoteStream = null;
        }
      }
      if (peerConnection) {
        peerConnection.close();
        peerConnection = null;
      }
      if (dataChannel) {
        if ((dataChannel.readyState === 'open' || dataChannel.readyState === 'connecting') && dataChannel.close) {
          dataChannel.close();
        }
        dataChannel = null;
        yield put({
          type: actions.DATA_CHANNEL_STATE_IS_CLOSE,
        });
      }
      // Delete room on hangup
      if (roomId) {
        const telemedMainUser = yield select(getTelemedMainUserFromStore);
        const userType = yield select(getUserTypeFromStore);
        const calleeCandidates = yield call(getCalleeCandidatesFromFirestore, roomId, telemedMainUser);
        const calleeCandidatesArr = [];
        calleeCandidates.forEach(async (candidate) => {
          calleeCandidatesArr.push(candidate);
        });
        yield all(calleeCandidatesArr.map((candidate) => call(deleteCandidate, candidate)));
        // On patient retry/timedOut, keep the callerCandidates (doctor's): he
        // doesn't re-emit after gathering, so deleting them would leave the
        // patient's retry with no candidates from the other side to pair with.
        if (!(userType === 'patient' && (shouldRetry || timedOut))) {
          const callerCandidates = yield call(getCallerCandidatesFromFirestore, roomId, telemedMainUser);
          const callerCandidatesArr = [];
          callerCandidates.forEach(async (candidate) => {
            callerCandidatesArr.push(candidate);
          });
          yield all(callerCandidatesArr.map((candidate) => call(deleteCandidate, candidate)));
        }
        // The doctor "owns" the room. If the patient deletes it on retry, the
        // doctor sees !exists in the roomListener and fires an extra END(retry),
        // causing the cascade ("Reconnecting" 2×). An explicit patient end (no
        // retry) still deletes normally.
        if (!(userType === 'patient' && (shouldRetry || timedOut))) {
          yield call(deleteRoom, roomId, telemedMainUser);
        }
        if (shouldRetry) {
          if (userType === 'patient') {
            yield put({
              type: actions.JOIN_TELEMED_ROOM_REQUEST,
              payload: { roomId, mainUser: telemedMainUser },
            });
          } else {
            yield put({
              type: actions.CREATE_TELEMED_ROOM_REQUEST,
              payload: { roomId },
            });
          }
        } else if (timedOut) {
          // Soft stop after the 2-minute deadline: keep modal + media, show the
          // manual "Tentar novamente" button (handled by the reducer flag).
          notification(
            'warning',
            'Não foi possível conectar',
            'Toque em "Tentar novamente" para refazer a conexão.',
          );
          yield put({ type: actions.TELEMED_CONNECTION_TIMED_OUT_SUCCESS });
        } else {
          // Real end of the consult: reset the per-consult telemetry flags so the
          // next consultation is counted fresh.
          telemedConsultCounted = false;
          telemedTurnCounted = false;
          yield put({ type: actions.END_TELEMED_ROOM_SUCCESS });
        }
      }
    } catch (error) {
      console.warn(error);
      // No notification after logout (expected permission failures).
      if (getAuth().currentUser) {
        notification('error', 'Algo deu errado ao tentar encerrar a chamada', 'Tente novamente mais tarde.');
      }
      yield put({
        type: actions.END_TELEMED_ROOM_ERROR,
      });
    }
  });
}

// Logout with an active call: reuse hangUp so peerConnection/media are released.
export function* endTelemedOnLogout() {
  yield takeEvery(authActions.LOGOUT, function* () {
    const openTelemedModal = yield select((state) => state.Telemed.openTelemedModal);
    if (!openTelemedModal) return;
    const roomId = yield select((state) => state.Telemed.roomId);
    yield put({
      type: actions.END_TELEMED_ROOM_REQUEST,
      payload: { roomId },
    });
  });
}

// Connection deadline. Doctor: armed on CREATE_TELEMED_ROOM_REQUEST. Patient:
// armed on JOINING_TELEMED_CREATE_PEER_CONNECTION_SUCCESS (PC created = offer
// existed), NOT on the "Acessar" click — so it covers only the "connecting" phase
// (real failure / zombie room), never the timeout-free wait for the doctor.
// Auto-retries underneath do NOT reset it; a connection re-arms it for the next drop.
export function* telemedConnectionDeadline() {
  while (true) {
    yield take([
      actions.CREATE_TELEMED_ROOM_REQUEST,
      actions.JOINING_TELEMED_CREATE_PEER_CONNECTION_SUCCESS,
    ]);
    const { timeout } = yield race({
      connected: take(actions.DATA_CHANNEL_STATE_IS_OPEN),
      ended: take([
        actions.END_TELEMED_ROOM_SUCCESS,
        appActions.CANCEL_LISTENERS,
      ]),
      timeout: call(sleep, CONNECTION_DEADLINE_MS),
    });
    if (timeout) {
      const roomId = yield select((state) => state.Telemed.roomId);
      yield put({
        type: actions.END_TELEMED_ROOM_REQUEST,
        payload: { roomId, timedOut: true },
      });
    }
  }
}

// "Still in consult?" modal on reaching MAX_CALL_DURATION_MS. Resolves true if
// the user confirms within MAX_DURATION_PROMPT_MS; otherwise (nobody answered =
// forgotten call) resolves false and the call is ended.
function promptKeepCallAlive() {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const modal = Modal.confirm({
      title: 'A teleconsulta está ativa há 3 horas',
      content: 'Deseja continuar? Se ninguém responder, a chamada será encerrada para evitar consumo desnecessário.',
      okText: 'Continuar',
      cancelText: 'Encerrar agora',
      onOk: () => finish(true),
      onCancel: () => finish(false),
    });
    setTimeout(() => {
      modal.destroy();
      finish(false);
    }, MAX_DURATION_PROMPT_MS);
  });
}

// Safety net for a forgotten open call: on connect (DATA_CHANNEL open) it arms a
// MAX_CALL_DURATION_MS limit. On hit, ask to continue; if nobody answers, really
// end it (frees the paid TURN relay). A real END (END_TELEMED_ROOM_SUCCESS)
// disarms and waits for the next call. Retries underneath do NOT reset the limit
// — total duration is what matters.
export function* telemedMaxDurationGuard() {
  while (true) {
    yield take(actions.DATA_CHANNEL_STATE_IS_OPEN);
    let keepGuarding = true;
    while (keepGuarding) {
      const { ended } = yield race({
        maxDuration: call(sleep, MAX_CALL_DURATION_MS),
        ended: take([
          actions.END_TELEMED_ROOM_SUCCESS,
          appActions.CANCEL_LISTENERS,
        ]),
      });
      if (ended) {
        keepGuarding = false;
        break;
      }
      const keepAlive = yield call(promptKeepCallAlive);
      if (!keepAlive) {
        const roomId = yield select((state) => state.Telemed.roomId);
        yield put({
          type: actions.END_TELEMED_ROOM_REQUEST,
          payload: { roomId },
        });
        keepGuarding = false;
      }
    }
  }
}

// Manual retry triggered by the "Tentar novamente" button. Re-runs the proper
// flow based on the user type (doctor creates the room, patient joins it). The
// CREATE/JOIN reducers clear `connectionTimedOut`.
export function* retryTelemedConnection() {
  yield takeLatest(actions.RETRY_TELEMED_CONNECTION, function* () {
    const userType = yield select(getUserTypeFromStore);
    const telemedMainUser = yield select(getTelemedMainUserFromStore);
    const roomId = yield select((state) => state.Telemed.roomId);
    if (!roomId) return;
    if (userType === 'patient') {
      yield put({
        type: actions.JOIN_TELEMED_ROOM_REQUEST,
        payload: { roomId, mainUser: telemedMainUser },
      });
    } else {
      yield put({
        type: actions.CREATE_TELEMED_ROOM_REQUEST,
        payload: { roomId },
      });
    }
  });
}

// Doctor: real-time listener on users/{uid}/telemedPresence. Patients show up here
// (written by the cloud function) when they enter the waiting room.
function createTelemedPresenceListener(mainUser) {
  const db = getDatabase();
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }
  const presenceRef = ref(db, `users/${uid}/telemedPresence`);
  return eventChannel((emit) => {
    const unsubscribe = onValue(presenceRef, (snapshot) => {
      emit(snapshot.val() || {});
    });
    return () => unsubscribe();
  });
}

function removeTelemedPresenceFromDB(mainUser, roomId) {
  const db = getDatabase();
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }
  return remove(ref(db, `users/${uid}/telemedPresence/${roomId}`));
}

// Opens the telemed modal straight from the notification. Only roomId is needed to
// connect; userInfo (from the presence node) just feeds the name/WhatsApp display.
function openTelemedFromPresence(roomId, entry) {
  telemedActionChannel.put(
    actions.setOpenTelemedModal(true, roomId, {
      queryConfirmedKey: roomId,
      userInfo: (entry && entry.userInfo) || null,
    }),
  );
  notificationAntd.destroy(`telemed-presence-${roomId}`);
}

// Doctor-side listener, started once the agenda is ready (SELECT_AGENDA), like the
// other agenda listeners. Keeps state.Telemed.telemedPresence in sync (agenda badge,
// scoped per rendered event) and toasts once per patient — but only to the doctor
// who will attend it (entry.professional === own uid), so co-workers under the same
// master account aren't interrupted. A SWEEP timer re-checks staleness even with no
// RTDB event, so a flag without heartbeats clears (crash / network drop / no beacon).
export function* watchTelemedPresence() {
  let currentListener = null;
  let toasted = {};
  yield takeLatest(agendaActions.SELECT_AGENDA, function* () {
    if (currentListener) {
      currentListener.close();
      currentListener = null;
    }
    const professional = yield select(getIsProfessionalFromStore);
    if (!professional) return;
    const mainUser = yield select(getMainUserFromStore);
    const auth = getAuth();
    const ownUid = auth.currentUser && auth.currentUser.uid;
    currentListener = yield call(createTelemedPresenceListener, mainUser);
    let rawMap = {};
    let lastKeys = '';
    function* applyPresence() {
      const now = Date.now();
      const fresh = {};
      const staleRoomIds = [];
      Object.keys(rawMap).forEach((roomId) => {
        const entry = rawMap[roomId];
        if (!entry || entry.status !== 'waiting') return;
        if (now - (entry.enteredAt || 0) > PRESENCE_STALE_MS) {
          staleRoomIds.push(roomId);
          return;
        }
        fresh[roomId] = entry;
      });
      // Delete ghost flags (no heartbeat past staleness) from the DB, not just hide
      // them. Self-healing: if the patient comes back, the heartbeat recreates it.
      if (staleRoomIds.length) {
        try {
          yield all(staleRoomIds.map((roomId) => (
            call(removeTelemedPresenceFromDB, mainUser, roomId)
          )));
        } catch (error) {
          console.warn('clearStaleTelemedPresence failed', error);
        }
      }
      // Mark that the doctor saw the patient waiting (no-show vs failed-connect).
      // Setter self-gates on the active record's room, so calling per room is safe.
      Object.keys(fresh).forEach((roomId) => recordPatientPresenceSeen(roomId));
      // Only push to the store when the set of waiting rooms changes — avoids
      // re-rendering the agenda on every heartbeat / sweep tick.
      const keys = Object.keys(fresh).sort().join('|');
      if (keys !== lastKeys) {
        lastKeys = keys;
        yield put({ type: actions.TELEMED_PRESENCE_UPDATED, payload: fresh });
      }
      // Room the doctor currently has open in the telemed modal — its patient is
      // being attended, so a "waiting" toast for it only confuses (esp. while
      // connecting). Other rooms still toast.
      const openModalRoomId = yield select((state) => (
        state.Telemed.openTelemedModal ? state.Telemed.roomId : null
      ));
      const nextToasted = {};
      Object.keys(fresh).forEach((roomId) => {
        const entry = fresh[roomId];
        // Only toast the doctor who will attend this patient.
        if (entry.professional !== ownUid) return;
        // Skip the room already open in the modal; dismiss any leftover toast.
        if (roomId === openModalRoomId) {
          notificationAntd.destroy(`telemed-presence-${roomId}`);
          return;
        }
        nextToasted[roomId] = true;
        if (!toasted[roomId]) {
          const name = entry.patientName || 'Paciente';
          notificationAntd.info({
            message: 'Paciente aguardando',
            description: (
              <span>
                {`${name} entrou na teleconsulta. `}
                <Button
                  type="link"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => openTelemedFromPresence(roomId, entry)}
                >
                  Abrir teleconsulta
                </Button>
              </span>
            ),
            duration: null,
            key: `telemed-presence-${roomId}`,
          });
        }
      });
      // Dismiss sticky toasts whose patient left/connected/went stale.
      Object.keys(toasted).forEach((roomId) => {
        if (!nextToasted[roomId]) notificationAntd.destroy(`telemed-presence-${roomId}`);
      });
      toasted = nextToasted;
    }
    yield takeEvery(currentListener, function* (presenceMap) {
      rawMap = presenceMap || {};
      yield* applyPresence();
    });
    while (true) {
      const { stop } = yield race({
        tick: call(sleep, PRESENCE_SWEEP_MS),
        stop: take([appActions.CANCEL_LISTENERS]),
      });
      if (stop) break;
      yield* applyPresence();
    }
    currentListener.close();
    currentListener = null;
    toasted = {};
  });
}

// Patient leaving (tab close / F5): the React unload handler dispatches this and
// we fire the 'gone' here (orchestration stays in the saga). keepalive survives
// the unload; the doctor's listener clears the badge when the node is removed.
export function* leaveTelemedPresence() {
  yield takeEvery(actions.LEAVE_TELEMED_PRESENCE, function* (action) {
    const { roomId, mainUser } = action.payload;
    if (!roomId || !mainUser) return;
    try {
      yield call(setTelemedPresenceGoneOnUnload, { mainUser, roomId });
    } catch (error) {
      console.warn('leaveTelemedPresence failed', error);
    }
  });
}

// Unload (pagehide): axios flushes get killed on tab close, so flush diagnostics
// and funnel via keepalive beacons. Each self-gates (doctor has no funnel; a
// pre-webrtc patient has no diagnostics record). Fired on every pagehide.
export function* flushTelemedOnUnload() {
  yield takeEvery(actions.FLUSH_TELEMED_ON_UNLOAD, function* () {
    try {
      yield call(flushDiagnosticsBeacon);
      yield call(flushPatientFunnelBeacon, 'unload');
    } catch (error) {
      console.warn('flushTelemedOnUnload failed', error);
    }
  });
}

// Patient heartbeat: while waiting for the doctor, re-write presence every
// HEARTBEAT_MS so the doctor knows the patient is still there (refreshes enteredAt).
// Stops once connecting/ended; once it stops, the flag goes stale on the doctor side.
export function* telemedPresenceHeartbeat() {
  yield takeLatest(actions.TELEMED_WAITING_FOR_DOCTOR, function* () {
    while (true) {
      const { stop } = yield race({
        beat: call(sleep, HEARTBEAT_MS),
        stop: take([
          actions.JOINING_TELEMED_CREATE_PEER_CONNECTION_SUCCESS,
          actions.DATA_CHANNEL_STATE_IS_OPEN,
          actions.END_TELEMED_ROOM_REQUEST,
          appActions.CANCEL_LISTENERS,
        ]),
      });
      if (stop) return;
      const mainUser = yield select(getTelemedMainUserFromStore);
      const roomId = yield select((state) => state.Telemed.roomId);
      if (mainUser && roomId) {
        try {
          yield call(setTelemedPresenceRequest, { mainUser, roomId, status: 'waiting' });
        } catch (error) {
          console.warn('telemed presence heartbeat failed', error);
        }
      }
    }
  });
}

// Doctor (authenticated) clears the presence flag once connected — robust cleanup
// that does not depend on the anonymous patient's best-effort 'gone'.
export function* clearTelemedPresenceOnConnect() {
  yield takeEvery(actions.DATA_CHANNEL_STATE_IS_OPEN, function* () {
    const userType = yield select(getUserTypeFromStore);
    if (userType !== 'professional') return;
    const telemedMainUser = yield select(getTelemedMainUserFromStore);
    const roomId = yield select((state) => state.Telemed.roomId);
    if (!telemedMainUser || !roomId) return;
    try {
      yield call(removeTelemedPresenceFromDB, telemedMainUser, roomId);
      notificationAntd.destroy(`telemed-presence-${roomId}`);
    } catch (error) {
      console.warn('clearTelemedPresence (doctor connect) failed', error);
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(validatedPatientCpfRequest),
    fork(openUserMedia),
    fork(createRoom),
    fork(createConnectionStateChange),
    fork(createDataChannel),
    fork(createDataChannelOpenStateChange),
    fork(createDataChannelCloseStateChange),
    fork(createDataChannelMessageListener),
    fork(getCallerEventCandidates),
    fork(getRemoteTrack),
    fork(getSessionDescription),
    fork(getCalleeRemoteCandidates),
    fork(joinRoomById),
    fork(checkRoomSessionUuid),
    fork(getCalleeEventCandidates),
    fork(getCallerRemoteCandidates),
    fork(stopOwnWebcam),
    fork(startOwnWebcam),
    fork(muteOwnMic),
    fork(unmuteOwnMic),
    fork(sendChatMessage),
    fork(receivedChatMessage),
    fork(hangUp),
    fork(endTelemedOnLogout),
    fork(watchDataChannelMessagesChannel),
    fork(watchTelemedActionChannel),
    fork(telemedConnectionDeadline),
    fork(telemedMaxDurationGuard),
    fork(retryTelemedConnection),
    fork(enableRemoteAudio),
    fork(watchTelemedPresence),
    fork(clearTelemedPresenceOnConnect),
    fork(leaveTelemedPresence),
    fork(flushTelemedOnUnload),
    fork(telemedPresenceHeartbeat),
  ]);
}
